/**
 * IzyRent Accordion Wrapper
 * This script wraps izyRent addon groups into accordion dropdowns
 */

class IzyRentAccordion {
  constructor() {
    this.initialized = false;
    this.observer = null;
    /** @type {string[]} */
    this.metafieldTitles = [];
    const productDetails = document.querySelector('.product-details');
    if (productDetails) {
      try {
        const titlesJson = productDetails.getAttribute('data-accordion-titles');
        if (titlesJson) {
          const titles = JSON.parse(titlesJson);
          titles.forEach((/** @type {string} */ title, /** @type {number} */ index) => {
            this.metafieldTitles[index + 1] = title || '';
          });
        }
      } catch (e) {
        console.warn('Error parsing accordion titles:', e);
      }
    }
    this.accordionCount = 0;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.startChecking();
  }

  startChecking() {
    this.transformGroups();
    
    const checkIntervals = [100, 300, 500, 1000, 2000];
    checkIntervals.forEach(delay => {
      setTimeout(() => this.transformGroups(), delay);
    });
    
    this.observeForChanges();
  }

  observeForChanges() {
    const setupObserver = () => {
      const izyRentContainer = document.querySelector('[id*="izyAddons"]');
      
      if (!izyRentContainer) {
        return false;
      }

      if (this.observer) {
        return true;
      }

      this.observer = new MutationObserver((mutations) => {
        let shouldTransform = false;
        
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node instanceof Element && node.classList.contains('izyAddonGroup')) {
                shouldTransform = true;
              }
            });
          }
        });

        if (shouldTransform) {
          this.transformGroups();
        }
      });

      this.observer.observe(izyRentContainer, {
        childList: true,
        subtree: true
      });
      
      return true;
    };

    const trySetup = () => {
      if (!setupObserver()) {
        setTimeout(trySetup, 500);
      }
    };
    
    trySetup();
  }

  transformGroups() {
    const addonGroups = document.querySelectorAll('div[class*="izyAddonGroup"][data-group]');
    
    if (addonGroups.length === 0) {
      return;
    }

    const groupsMap = new Map();
    
    addonGroups.forEach((group) => {
      const groupId = group.getAttribute('data-group');
      if (!groupId) return;
      
      if (group.hasAttribute('data-accordion-processed')) return;
      
      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, []);
      }
      groupsMap.get(groupId).push(group);
      
      group.setAttribute('data-accordion-processed', 'true');
    });

    groupsMap.forEach((groups, groupId) => {
      this.createAccordionForGroup(groups, groupId);
    });

    this.initialized = true;
  }

  
  createAccordionForGroup(groups, groupId) {
    if (!groups || groups.length === 0) {
      return;
    }

    const firstGroup = groups[0];
    if (!firstGroup || firstGroup.closest('accordion-custom')) {
      return;
    }

    this.accordionCount++;
    
    let groupTitle;
    const metafieldTitle = this.accordionCount >= 1 && this.accordionCount <= 10 ? this.metafieldTitles[this.accordionCount] : '';
    
    if (metafieldTitle) {
      groupTitle = metafieldTitle;
    } else {
      groupTitle = `Group ${groupId}`;
      const firstAddon = firstGroup.querySelector('[data-title]');
      if (firstAddon) {
        const title = firstAddon.getAttribute('data-title');
        groupTitle = this.extractGroupTitle(title, groupId);
      }
    }

    const accordionCustom = document.createElement('accordion-custom');
    
    const details = document.createElement('details');
    details.className = 'details izyrent-group-details';
    details.open = false;
    
    const summary = document.createElement('summary');
    summary.className = 'details__header izyrent-group-header';
    summary.innerHTML = `
      ${groupTitle}
      <span class="svg-wrapper icon-caret icon-animated">
        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor">
        </svg>
      </span>
    `;
    
    const detailsContent = document.createElement('div');
    detailsContent.className = 'details-content izyrent-group-content';
    
    const parentNode = firstGroup.parentNode;
    if (parentNode) {
      parentNode.insertBefore(accordionCustom, firstGroup);
    }
    
    groups.forEach((group) => {
      if (group) {
        detailsContent.appendChild(group);
      }
    });
    
    details.appendChild(summary);
    details.appendChild(detailsContent);
    accordionCustom.appendChild(details);
    
    setTimeout(() => {
      this.processGroupPrices(detailsContent);
    }, 100);
  }

  processGroupPrices(container) {
    const priceSelectors = [
      '[class*="price"]',
      '[class*="Price"]',
      '[class*="cost"]',
      '[class*="Cost"]',
      '[class*="amount"]',
      '[class*="Amount"]',
      'span:not([class*="icon"])',
      'div:not([class*="icon"])'
    ];
    
    priceSelectors.forEach(selector => {
      const priceElements = container.querySelectorAll(selector);
      priceElements.forEach(element => {
        const text = element.textContent || (element instanceof HTMLElement ? element.innerText : '');
        if (text && text.trim()) {          
          const zeroPricePatterns = [
            { pattern: /^[+]?\s*\$\s*0\.00\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*\$\s*0\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*€\s*0\.00\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*€\s*0\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*£\s*0\.00\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*£\s*0\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*¥\s*0\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*₹\s*0\s*$/i, replacement: 'Free' },
            { pattern: /^[+]?\s*0\.00\s*$/i, replacement: 'Free' }
          ];
         
          for (const { pattern, replacement } of zeroPricePatterns) {
            if (pattern.test(text.trim())) {
              element.textContent = replacement;
              break;
            }
          }
        }
      });
    });
  }
 
  extractGroupTitle(title, groupId) {    
    if (!title) return `Additional Options ${groupId}`;
    
    const separators = [' - ', ' / ', ' | '];
    for (const sep of separators) {
      if (title.includes(sep)) {
        const parts = title.split(sep);
        if (parts.length > 0 && parts[0]) {
          const firstPart = parts[0].trim();
          if (firstPart.length > 3) {
            return `${firstPart} Options`;
          }
        }
      }
    }
    
    if (title) {
      const words = title.split(' ');
      if (words.length >= 2) {
        const groupName = words.slice(0, Math.min(3, words.length)).join(' ');
        return `${groupName} Options`;
      }
    }
    return `Additional Options ${groupId}`;
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

const initIzyRentAccordion = () => {
  if (!customElements.get('accordion-custom')) {
    setTimeout(initIzyRentAccordion, 100);
  } else {
    new IzyRentAccordion();
  }
};

initIzyRentAccordion();

